import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'
import { buildRouteWithListScopeFromRoute } from '@/shared/scope/listScope'
import { SECONDARY_MENU_DATA, findNavigationOverride } from '../constants'
import { Home } from 'lucide-vue-next'
import type { BreadcrumbItem } from '../types'

const activeModuleId = ref('dashboards')
const breadcrumbItems = ref<BreadcrumbItem[]>([])
const activeItemLabel = ref<string>('')
const expandedItem = ref<string | null>(null)

export function normalizeNavPath(path: string): string {
  return path.replace(/\/$/, '') || '/'
}

/** Exact match or child path of a menu href (slash-bounded). */
export function pathMatchesMenuHref(path: string, href?: string): boolean {
  if (!href || href === '#' || href === '/') return false
  const normalizedPath = normalizeNavPath(path)
  const normalizedHref = normalizeNavPath(href)
  return normalizedPath === normalizedHref || normalizedPath.startsWith(`${normalizedHref}/`)
}

type MenuMatch = {
  itemLabel: string
  itemHref: string
  parentLabel?: string
  parentHref?: string
  hrefLength: number
}

/**
 * Composable to manage application-wide navigation state, including module selection,
 * breadcrumbs, and route synchronization.
 */
export function useNavigation() {
  const route = useRoute()

  /**
   * Helper to determine the correct module ID based on a URL path
   */
  const findModuleIdFromPath = (path: string): string => {
    const normalizedPath = normalizeNavPath(path)

    // Root-level dashboard check
    if (normalizedPath === '/' || normalizedPath === '/dashboard') return 'dashboards'

    const override = findNavigationOverride(normalizedPath)
    if (override) return override.moduleId

    for (const [moduleId, menuData] of Object.entries(SECONDARY_MENU_DATA)) {
      for (const item of menuData.items) {
        if (pathMatchesMenuHref(normalizedPath, item.href)) {
          return moduleId
        }

        if (item.items) {
          for (const subItem of item.items) {
            if (pathMatchesMenuHref(normalizedPath, subItem.href)) {
              return moduleId
            }
          }
        }
      }
    }
    return 'dashboards'
  }

  /** Longest-prefix menu leaf (or top-level item) for the current path. */
  const findBestMenuMatch = (path: string, moduleId: string): MenuMatch | null => {
    const menuData = SECONDARY_MENU_DATA[moduleId]
    if (!menuData) return null

    const candidates: MenuMatch[] = []

    for (const item of menuData.items) {
      if (pathMatchesMenuHref(path, item.href)) {
        candidates.push({
          itemLabel: item.label,
          itemHref: item.href as string,
          hrefLength: normalizeNavPath(item.href as string).length
        })
      }

      if (item.items) {
        for (const subItem of item.items) {
          if (pathMatchesMenuHref(path, subItem.href)) {
            candidates.push({
              itemLabel: subItem.label,
              itemHref: subItem.href as string,
              parentLabel: item.label,
              parentHref: item.href,
              hrefLength: normalizeNavPath(subItem.href as string).length
            })
          }
        }
      }
    }

    if (candidates.length === 0) return null
    return candidates.reduce((best, current) =>
      current.hrefLength > best.hrefLength ? current : best
    )
  }

  /** Append current list scope to breadcrumb hrefs when the destination supports it. */
  const toScopedHref = (href?: string): string | undefined => {
    if (!href || href === '#') return href
    const target: RouteLocationRaw = buildRouteWithListScopeFromRoute(href, route)
    if (typeof target === 'string') return target
    if ('path' in target && target.path) {
      const query = target.query as Record<string, string> | undefined
      if (!query || Object.keys(query).length === 0) return target.path
      const params = new URLSearchParams(query)
      return `${target.path}?${params.toString()}`
    }
    return href
  }

  /**
   * Updates breadcrumbs based on selected menu items
   */
  const updateBreadcrumbs = (payload: {
    parentLabel?: string
    parentHref?: string
    itemLabel: string
    href?: string
  }) => {
    const items: BreadcrumbItem[] = [{ label: 'Dashboard', icon: Home, href: '/dashboard' }]

    if (payload.parentLabel && payload.parentHref && payload.parentHref !== '#') {
      items.push({ label: payload.parentLabel, href: toScopedHref(payload.parentHref) })
    }

    items.push({
      label: payload.itemLabel,
      href: toScopedHref(payload.href) || '#',
      isCurrent: true
    })
    breadcrumbItems.value = items
  }

  /**
   * Sets a basic dashboard breadcrumb as fallback
   */
  const setDefaultBreadcrumb = () => {
    breadcrumbItems.value = [{ label: 'Dashboard', icon: Home, href: '/dashboard' }]
  }

  /**
   * Coerces a possible `LocationQueryValue` (string | string[] | null) down to a single string.
   */
  const queryString = (value: unknown): string | undefined => {
    if (typeof value === 'string' && value.trim().length > 0) return value
    if (Array.isArray(value) && typeof value[0] === 'string' && value[0].trim().length > 0) {
      return value[0]
    }
    return undefined
  }

  /**
   * Core logic to keep layout state in sync with the current URL
   */
  const syncWithRoute = () => {
    const currentPath = route.path

    // Coming-soon screens are global placeholders — they don't map to any
    // SECONDARY_MENU_DATA entry, so rely on the navigation context that
    // was carried over via query params (module / feature / parent).
    if (route.name === 'coming-soon' || currentPath === '/coming-soon') {
      const moduleFromQuery = queryString(route.query.module)
      const featureFromQuery = queryString(route.query.feature)
      const parentFromQuery = queryString(route.query.parent)

      if (moduleFromQuery && SECONDARY_MENU_DATA[moduleFromQuery]) {
        activeModuleId.value = moduleFromQuery
      }

      if (featureFromQuery) {
        activeItemLabel.value = featureFromQuery
        if (parentFromQuery) {
          expandedItem.value = parentFromQuery
        }
        updateBreadcrumbs({
          parentLabel: parentFromQuery,
          itemLabel: featureFromQuery
        })
      } else {
        setDefaultBreadcrumb()
        activeItemLabel.value = ''
      }
      return
    }

    const moduleId = findModuleIdFromPath(currentPath)
    activeModuleId.value = moduleId

    const navigationOverride = findNavigationOverride(currentPath)
    if (navigationOverride) {
      const overrideMenu = SECONDARY_MENU_DATA[navigationOverride.moduleId]
      const menuItem = overrideMenu?.items.find(
        (item) =>
          normalizeNavPath(item.href || '') === normalizeNavPath(navigationOverride.menuHref)
      )
      if (menuItem) {
        activeItemLabel.value = menuItem.label
        expandedItem.value = null
        updateBreadcrumbs({ itemLabel: menuItem.label, href: toScopedHref(menuItem.href) })
        return
      }
    }

    const match = findBestMenuMatch(currentPath, moduleId)
    if (match) {
      activeItemLabel.value = match.itemLabel
      if (match.parentLabel) {
        expandedItem.value = match.parentLabel
      }
      updateBreadcrumbs({
        parentLabel: match.parentLabel,
        parentHref: toScopedHref(match.parentHref),
        itemLabel: match.itemLabel,
        href: toScopedHref(match.itemHref)
      })
      return
    }

    activeItemLabel.value = ''
    setDefaultBreadcrumb()
  }

  // Switches active module and resets breadcrumb/sidebar selection.
  const handleModuleSelect = (id: string) => {
    activeModuleId.value = id
    setDefaultBreadcrumb()
    activeItemLabel.value = ''
  }

  // Expands or collapses a nested sidebar group by label.
  const toggleExpand = (label: string) => {
    expandedItem.value = expandedItem.value === label ? null : label
  }

  // Keep layout state in sync with the route. Each caller registers a watch so
  // remounts (e.g. layout HMR) always re-bind — sync is idempotent on shared refs.
  watch(() => [route.path, route.name] as const, syncWithRoute, { immediate: true })

  return {
    activeModuleId,
    breadcrumbItems,
    activeItemLabel,
    expandedItem,
    handleModuleSelect,
    toggleExpand,
    syncWithRoute,
    pathMatchesMenuHref: (href?: string) => pathMatchesMenuHref(route.path, href)
  }
}
